import React, { useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';

import { ErrorUtils } from 'utils';

import { topUpBalanceByCreditCard } from 'features/finance/api';

import { PaymentTypesOptions } from 'features/finance/constants/payment-types-options';
import { TopUpBalanceAmountPart } from 'features/finance/components/TopUpBalanceAmountPart';
import { TopUpBalanceCardDetailsPart } from 'features/finance/components/TopUpBalanceCardDetailsPart';

interface TopUpBalanceFormProps {
  onSubmit?: () => void;
  onProcessing?: () => void;
  onTopUpFailed: (message?: string) => void;
  onTopUpSuccess: (amount: number, currency: string) => void;
}

type TopUpBalanceInput = {
  amount: number;
  payment_method: typeof PaymentTypesOptions[0];
  card_holder_name: string;
  currency: {
    code: string;
  } | null;
};

const DEFAULT_VALUES: TopUpBalanceInput = {
  amount: 0,
  payment_method: PaymentTypesOptions[0],
  card_holder_name: '',
  currency: null,
};

export const TopUpBalanceForm: React.FC<TopUpBalanceFormProps> = ({
  onSubmit: onFormSubmit,
  onTopUpSuccess,
  onTopUpFailed,
  onProcessing,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [step, setStep] = useState(1);
  const [isPaying, setIsPaying] = useState(false);
  const payingRef = useRef(false);

  const form = useForm<TopUpBalanceInput>({
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES,
  });

  if (!stripe || !elements) {
    return <></>;
  }

  const onSubmit = async () => {
    const cardElement = elements.getElement(CardElement);
    const { amount, card_holder_name, currency } = form.getValues();
    const currencyCode = currency?.code;

    if (!cardElement || !currencyCode || payingRef.current) return;

    payingRef.current = true;
    setIsPaying(true);
    let redirecting = false;

    try {
      const result = await topUpBalanceByCreditCard({
        currency: currencyCode,
        amount: Number(amount),
      });

      const intent = await stripe.confirmCardPayment(
        result.data.client_secret,
        {
          payment_method: {
            card: cardElement,
            billing_details: { name: card_holder_name },
          },
          return_url: window.location.href,
        },
        { handleActions: false }
      );

      const nextAction = intent.paymentIntent?.next_action;
      const redirectUrl = nextAction?.type === 'redirect_to_url' ? nextAction.redirect_to_url?.url : '';

      if (redirectUrl) {
        localStorage.setItem('latestTopUpAmount', String(amount));
        redirecting = true;
        onProcessing?.();
        window.location.href = redirectUrl;
        return;
      }

      if (intent.error) {
        onTopUpFailed(intent.error.message ?? '');
      } else if (!nextAction) {
        onFormSubmit?.();
        onTopUpSuccess(Number(amount), currencyCode);
      }
    } catch (err) {
      onTopUpFailed(ErrorUtils.convertError(err));
    } finally {
      if (!redirecting) {
        payingRef.current = false;
        setIsPaying(false);
      }
    }
  };

  const onFirstStep = () => {
    step === 2 && setStep(1);
  };

  const onSecondStep = () => {
    step === 1 && setStep(2);
  };

  return (
    <FormProvider {...form}>
      <form>
        {step === 1 ? (
          <TopUpBalanceAmountPart onNextStep={onSecondStep} />
        ) : step === 2 ? (
          <TopUpBalanceCardDetailsPart onFormSubmit={onSubmit} onPreviousStep={onFirstStep} isPaying={isPaying} />
        ) : null}
      </form>
    </FormProvider>
  );
};
